import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'antd';
import IconFont from '$/components/IconFont';
import {
  getOfficialAssetCategoryList,
  getOfficialAssetGenreList,
  getOfficialAssetMaterialList,
  getOfficialAssetTagList,
} from '$/api/canvasRquest';
import type {
  OfficialAssetMaterialListData,
  OfficialAssetServerCategory,
  OfficialAssetServerGenre,
  OfficialAssetServerMaterial,
  OfficialAssetServerTagGroup,
} from '$/api/server.types';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import CategoryTabs from '../../OfficialAssetContent/CategoryTabs';
import MaterialList from '../../OfficialAssetContent/MaterialList';
import TagFilterBar from '../../OfficialAssetContent/TagFilterBar';
import type { OfficialAssetSelectedTags } from '../../OfficialAssetContent/types';
import type { RoleGenMaterialCategory, RoleGenMaterialFilter } from './RoleSettingForm';
import { resolveDefaultSubject } from './officialMaterialCategory';
import styles from './index.module.scss';

const ROLE_MATERIAL_SELECTOR_Z_INDEX = 5700;
const ROLE_MATERIAL_FILTER_POPOVER_Z_INDEX = ROLE_MATERIAL_SELECTOR_Z_INDEX + 1;
const ROLE_MATERIAL_SELECTOR_PAGE_SIZE = 50;
const ROLE_MATERIAL_SELECTOR_SCROLL_LOAD_THRESHOLD = 400;

interface OfficialMaterialSelectModalProps {
  open: boolean;
  category: RoleGenMaterialCategory;
  selectedMaterialId?: string;
  selectedFilter?: RoleGenMaterialFilter;
  onClose: () => void;
  onConfirm: (material: OfficialAssetServerMaterial, filter: RoleGenMaterialFilter) => void;
}

/** 复制标签筛选值，避免弹框内修改影响表单中已确认的筛选快照。 */
function cloneSelectedTags(tags?: OfficialAssetSelectedTags): OfficialAssetSelectedTags {
  return Object.fromEntries(
    Object.entries(tags ?? {}).map(([key, values]) => [key, [...values]]),
  );
}

/** 使用统一的筛选参数请求一页创建角色官方素材。 */
function getRoleMaterialPage(
  genreId: number,
  tags: OfficialAssetSelectedTags,
  keyword: string,
  cursor = '',
): Promise<OfficialAssetMaterialListData> {
  return getOfficialAssetMaterialList({
    genre_id: genreId,
    count: ROLE_MATERIAL_SELECTOR_PAGE_SIZE,
    cursor,
    filters: tags,
    keyword: keyword || undefined,
  });
}

/**
 * 创建角色素材选择弹框；复用官方资产库分类、标签和素材列表接口，只保留单选确认流程。
 */
function OfficialMaterialSelectModal({
  open,
  category,
  selectedMaterialId,
  selectedFilter,
  onClose,
  onConfirm,
}: OfficialMaterialSelectModalProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const modulesRef = useRef<HTMLDivElement>(null);
  const categoryFetchEpochRef = useRef(0);
  const tagFetchEpochRef = useRef(0);
  const materialFetchEpochRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);
  const searchResultTotalRef = useRef<number | null>(null);
  const autoLocateMaterialIdRef = useRef('');
  const [categories, setCategories] = useState<OfficialAssetServerGenre[]>([]);
  const [tagGroups, setTagGroups] = useState<OfficialAssetServerTagGroup[]>([]);
  const [materials, setMaterials] = useState<OfficialAssetServerMaterial[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<OfficialAssetServerCategory | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<OfficialAssetServerGenre | null>(null);
  const [selectedTags, setSelectedTags] = useState<OfficialAssetSelectedTags>({});
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState<OfficialAssetServerMaterial | null>(null);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialLoadingMore, setMaterialLoadingMore] = useState(false);
  const [materialCursor, setMaterialCursor] = useState('');
  const [materialHasMore, setMaterialHasMore] = useState(false);
  const selectedSubjectCode = selectedSubject?.code ?? '';
  const selectedCategoryGenreId = selectedCategory?.id ?? null;
  const selectedCategoryGenre = selectedCategory?.genre ?? '';
  const materialListLoading = subjectsLoading || categoriesLoading || materialsLoading;
  const hasSelectedTags = useMemo(
    () => Object.values(selectedTags).some((values) => values.length > 0),
    [selectedTags],
  );
  const hasSearchKeyword = searchKeyword.length > 0;

  /** 把素材列表滚动区域恢复到顶部，配合分类和筛选切换后的首屏展示。 */
  const resetMaterialListPosition = useCallback((): void => {
    if (typeof window === 'undefined') {
      return;
    }
    window.requestAnimationFrame(() => {
      if (modulesRef.current) {
        modulesRef.current.scrollTop = 0;
      }
    });
  }, []);

  /** 打开弹框时加载官方资产一级分类，并按当前创建角色素材类型默认定位。 */
  useEffect(() => {
    let active = true;
    setCategories([]);
    setTagGroups([]);
    setMaterials([]);
    setSelectedSubject(null);
    setSelectedCategory(null);
    setSelectedTags(cloneSelectedTags(selectedFilter?.tags));
    setSearchValue(selectedFilter?.keyword ?? '');
    setSearchKeyword(selectedFilter?.keyword ?? '');
    searchResultTotalRef.current = null;
    autoLocateMaterialIdRef.current = selectedFilter ? selectedMaterialId ?? '' : '';
    setSelectedMaterial(null);
    setMaterialCursor('');
    setMaterialHasMore(false);

    if (!open) {
      setSubjectsLoading(false);
      return () => {
        active = false;
      };
    }

    /** 拉取官方资产一级分类，失败时保持空列表并提示用户。 */
    const loadSubjects = async (): Promise<void> => {
      setSubjectsLoading(true);
      try {
        const data = await getOfficialAssetCategoryList();
        if (!active) {
          return;
        }
        setSelectedSubject(resolveDefaultSubject(data.list, category));
      } catch {
        if (!active) {
          return;
        }
        setSelectedSubject(null);
        message.error(languageStore.smartcatTranslationText((value) => value.get_assets_list_failed));
      } finally {
        if (active) {
          setSubjectsLoading(false);
        }
      }
    };

    loadSubjects().catch(() => {});
    return () => {
      active = false;
    };
  }, [category, languageStore, open, selectedFilter, selectedMaterialId]);

  /** 一级分类变化后拉取题材列表，并默认选中该分类下的第一个题材。 */
  useEffect(() => {
    categoryFetchEpochRef.current += 1;
    const fetchEpoch = categoryFetchEpochRef.current;
    setCategories([]);
    setSelectedCategory(null);
    setTagGroups([]);
    setMaterials([]);
    setMaterialCursor('');
    setMaterialHasMore(false);
    setSelectedMaterial(null);

    if (!open || !selectedSubjectCode) {
      setCategoriesLoading(false);
      return;
    }

    /** 拉取当前一级分类下的题材列表，默认选中第一项以触发后续请求。 */
    const loadCategories = async (): Promise<void> => {
      setCategoriesLoading(true);
      try {
        const data = await getOfficialAssetGenreList(selectedSubjectCode);
        if (fetchEpoch !== categoryFetchEpochRef.current) {
          return;
        }
        setCategories(data.list);
        setSelectedCategory(
          data.list.find((item) => item.id === selectedFilter?.genreId) ?? data.list[0] ?? null,
        );
      } catch {
        if (fetchEpoch !== categoryFetchEpochRef.current) {
          return;
        }
        setCategories([]);
        setSelectedCategory(null);
        message.error(languageStore.smartcatTranslationText((value) => value.get_assets_list_failed));
      } finally {
        if (fetchEpoch === categoryFetchEpochRef.current) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories().catch(() => {});
  }, [languageStore, open, selectedFilter?.genreId, selectedSubjectCode]);

  /** 题材变化后拉取对应标签组，标签值由用户在当前弹框内自行选择。 */
  useEffect(() => {
    tagFetchEpochRef.current += 1;
    const fetchEpoch = tagFetchEpochRef.current;
    setTagGroups([]);

    if (!open || !selectedSubjectCode || !selectedCategoryGenre) {
      return;
    }

    /** 拉取当前题材对应的标签组，供官方资产筛选栏渲染。 */
    const loadTagGroups = async (): Promise<void> => {
      try {
        const data = await getOfficialAssetTagList({
          code: selectedSubjectCode,
          genre: selectedCategoryGenre,
        });
        if (fetchEpoch !== tagFetchEpochRef.current) {
          return;
        }
        setTagGroups(data.list);
      } catch {
        if (fetchEpoch !== tagFetchEpochRef.current) {
          return;
        }
        setTagGroups([]);
        message.error(languageStore.smartcatTranslationText((value) => value.get_assets_list_failed));
      }
    };

    loadTagGroups().catch(() => {});
  }, [languageStore, open, selectedCategoryGenre, selectedSubjectCode]);

  /** 题材、筛选或已提交关键词变化后拉取第一页官方素材。 */
  useEffect(() => {
    materialFetchEpochRef.current += 1;
    const fetchEpoch = materialFetchEpochRef.current;
    loadMoreInFlightRef.current = false;
    setMaterials([]);
    setMaterialCursor('');
    setMaterialHasMore(false);
    setSelectedMaterial(null);
    searchResultTotalRef.current = null;

    if (!open || !selectedCategoryGenreId) {
      setMaterialsLoading(false);
      setMaterialLoadingMore(false);
      return;
    }

    /** 拉取当前筛选结果；已有素材未命中时自动翻页，直到定位成功或列表结束。 */
    const loadMaterials = async (): Promise<void> => {
      setMaterialsLoading(true);
      setMaterialLoadingMore(false);
      try {
        const autoLocateMaterialId = autoLocateMaterialIdRef.current;
        const loadedMaterials: OfficialAssetServerMaterial[] = [];
        let cursor = '';
        let matchedMaterial: OfficialAssetServerMaterial | undefined;

        while (true) {
          // 自动定位依赖上一页返回的 cursor，必须保持串行请求。
          // eslint-disable-next-line no-await-in-loop
          const data = await getRoleMaterialPage(
            selectedCategoryGenreId,
            selectedTags,
            searchKeyword,
            cursor,
          );
          if (fetchEpoch !== materialFetchEpochRef.current) {
            return;
          }

          loadedMaterials.push(...data.list);
          matchedMaterial = data.list.find(
            (material) => String(material.id) === autoLocateMaterialId,
          );
          setMaterials([...loadedMaterials]);
          setMaterialCursor(data.cursor);
          setMaterialHasMore(Boolean(data.cursor));
          searchResultTotalRef.current = searchKeyword ? data.total ?? null : null;

          if (
            matchedMaterial
            || !autoLocateMaterialId
            || !data.cursor
            || data.cursor === cursor
          ) {
            break;
          }
          cursor = data.cursor;
          setMaterialLoadingMore(true);
        }

        setSelectedMaterial(matchedMaterial ?? loadedMaterials[0] ?? null);
      } catch {
        if (fetchEpoch !== materialFetchEpochRef.current) {
          return;
        }
        setMaterials([]);
        setMaterialCursor('');
        setMaterialHasMore(false);
        message.error(languageStore.smartcatTranslationText((value) => value.get_assets_list_failed));
      } finally {
        if (fetchEpoch === materialFetchEpochRef.current) {
          autoLocateMaterialIdRef.current = '';
          setMaterialsLoading(false);
          setMaterialLoadingMore(false);
        }
      }
    };

    loadMaterials().catch(() => {});
  }, [languageStore, open, searchKeyword, selectedCategoryGenreId, selectedTags]);

  /** 切换题材时保留已选标签值，只重置素材列表滚动位置。 */
  const handleCategoryChange = useCallback((nextCategory: OfficialAssetServerGenre): void => {
    if (selectedCategory?.id === nextCategory.id) {
      return;
    }
    autoLocateMaterialIdRef.current = '';
    setSelectedCategory(nextCategory);
    resetMaterialListPosition();
  }, [resetMaterialListPosition, selectedCategory]);

  /** 更新单个标签组选择值，并触发素材列表按服务端筛选重新加载。 */
  const handleTagGroupChange = useCallback((groupId: string, values: string[]): void => {
    autoLocateMaterialIdRef.current = '';
    setSelectedTags((currentTags) => {
      const nextTags = { ...currentTags };
      if (values.length > 0) {
        nextTags[groupId] = values;
      } else {
        delete nextTags[groupId];
      }
      return nextTags;
    });
    resetMaterialListPosition();
  }, [resetMaterialListPosition]);

  /** 清空单个标签组，选中值归零后控件恢复未选择展示并重新加载列表。 */
  const handleClearTagGroup = useCallback((groupId: string): void => {
    autoLocateMaterialIdRef.current = '';
    setSelectedTags((currentTags) => {
      if (!currentTags[groupId]?.length) {
        return currentTags;
      }
      const nextTags = { ...currentTags };
      delete nextTags[groupId];
      return nextTags;
    });
    resetMaterialListPosition();
  }, [resetMaterialListPosition]);

  /** 搜索输入时只更新文本，回车或失焦后再提交服务端搜索。 */
  const handleSearchChange = useCallback((value: string): void => {
    setSearchValue(value);
  }, []);

  /** 提交搜索关键词，相同关键词不重复请求。 */
  const handleSearch = useCallback((value: string): void => {
    const keyword = value.trim();
    if (keyword === searchKeyword) {
      return;
    }
    autoLocateMaterialIdRef.current = '';
    setSearchKeyword(keyword);
    searchResultTotalRef.current = null;
    resetMaterialListPosition();
  }, [resetMaterialListPosition, searchKeyword]);

  /** 滚动到底部阈值内时使用服务端 cursor 拉取下一页素材。 */
  const handleLoadMoreMaterials = useCallback(async (): Promise<void> => {
    if (
      !selectedCategoryGenreId
      || !materialHasMore
      || !materialCursor
      || materialsLoading
      || materialLoadingMore
      || loadMoreInFlightRef.current
    ) {
      return;
    }

    const fetchEpoch = materialFetchEpochRef.current;
    loadMoreInFlightRef.current = true;
    setMaterialLoadingMore(true);
    try {
      const data = await getRoleMaterialPage(
        selectedCategoryGenreId,
        selectedTags,
        searchKeyword,
        materialCursor,
      );
      if (fetchEpoch !== materialFetchEpochRef.current) {
        return;
      }
      setMaterials((currentMaterials) => [...currentMaterials, ...data.list]);
      setMaterialCursor(data.cursor);
      setMaterialHasMore(Boolean(data.cursor));
      if (searchKeyword && data.total !== undefined) {
        searchResultTotalRef.current = data.total;
      }
    } catch {
      if (fetchEpoch === materialFetchEpochRef.current) {
        message.error(languageStore.smartcatTranslationText((value) => value.load_more_fail));
      }
    } finally {
      if (fetchEpoch === materialFetchEpochRef.current) {
        loadMoreInFlightRef.current = false;
        setMaterialLoadingMore(false);
      }
    }
  }, [
    materialCursor,
    materialHasMore,
    materialLoadingMore,
    materialsLoading,
    languageStore,
    searchKeyword,
    selectedCategoryGenreId,
    selectedTags,
  ]);

  /** 监听弹框内容滚动，接近底部时触发官方素材分页加载。 */
  const handleModulesScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > ROLE_MATERIAL_SELECTOR_SCROLL_LOAD_THRESHOLD) {
      return;
    }
    handleLoadMoreMaterials().catch(() => {});
  }, [handleLoadMoreMaterials]);

  /** 点击官方素材卡片时更新当前单选素材。 */
  const handleMaterialClick = useCallback((material: OfficialAssetServerMaterial): void => {
    setSelectedMaterial(material);
  }, []);

  /** 确认当前选中官方素材并回填到创建角色表单。 */
  const handleConfirm = useCallback((): void => {
    if (!selectedMaterial) {
      return;
    }
    if (!selectedCategory) {
      return;
    }
    onConfirm(selectedMaterial, {
      genreId: selectedCategory.id,
      tags: cloneSelectedTags(selectedTags),
      keyword: searchKeyword || undefined,
    });
  }, [onConfirm, searchKeyword, selectedCategory, selectedMaterial, selectedTags]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={languageStore.smartcatTranslationText((value) => value.choose_reference_material)}
      width={1070}
      centered
      className={styles.materialSelectorModal}
      zIndex={ROLE_MATERIAL_SELECTOR_Z_INDEX}
      closeIcon={<IconFont name="crossBlack" fontSize={18} color="currentColor" />}
      destroyOnClose={false}
    >
      <div className={styles.materialSelectorContent}>
        <div ref={modulesRef} className={styles.materialSelectorBody} onScroll={handleModulesScroll}>
          <div className={styles.materialSelectorStickyHeader}>
            <CategoryTabs
              categories={categories}
              selectedCategoryId={selectedCategory?.id ?? null}
              searchValue={searchValue}
              onCategoryChange={handleCategoryChange}
              onSearchChange={handleSearchChange}
              onSearch={handleSearch}
            />
            <TagFilterBar
              tagGroups={tagGroups}
              selectedTags={selectedTags}
              onTagGroupChange={handleTagGroupChange}
              onClearTagGroup={handleClearTagGroup}
              popoverZIndex={ROLE_MATERIAL_FILTER_POPOVER_Z_INDEX}
            />
          </div>
          <MaterialList
            materials={materials}
            loading={materialListLoading}
            loadingMore={materialLoadingMore}
            emptyStateType={hasSearchKeyword || hasSelectedTags ? 'search' : 'preparing'}
            selectedMaterialId={selectedMaterial?.id}
            scrollToMaterialId={selectedFilter ? selectedMaterialId : undefined}
            scrollContainerRef={modulesRef}
            onMaterialClick={handleMaterialClick}
          />
        </div>
        <div className={styles.materialSelectorFooter}>
          <div className={styles.materialSelectorActions}>
            <button type="button" className={styles.materialSelectorCancelButton} onClick={onClose}>
              {languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_toast_cancel)}
            </button>
            <button
              type="button"
              className={styles.materialSelectorConfirmButton}
              disabled={!selectedMaterial}
              onClick={handleConfirm}
            >
              {languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_studio_tool_select)}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default OfficialMaterialSelectModal;
